import { IErrorResult, IIndexSignature, IWithApiData } from "Interfaces";
import { NotificationType, notify } from "src/Notifications";
import { isProduction } from "Data/Objects/System";

type RequestType = "DELETE" | "GET" | "POST" | "PATCH";

const baseURL = (isProduction)
	? "https://nephele.clintonelectronics.com/api"
	: "https://develop.nephele.clintonelectronics.com/api";

const baseHeaders = {
	"Content-Type": "application/vnd.api+json",
	"Accept": "application/vnd.api+json"
};

export function GET<R>(path: string, token: string, params?: IIndexSignature<string | number>) {
	return request<R>(path, "GET", null, null, params, token);
}

export function POST<R>(path: string, body?: unknown, token?: string) {
	return request<R>(path, "POST", JSON.stringify(body), null, null, token);
}

export function PATCH<R>(path: string, token: string, body?: unknown) {
	return request<R>(path, "PATCH", JSON.stringify(body), null, null, token);
}

export function DELETE<R>(path: string, token: string, params?: IIndexSignature<string | number>, body?: unknown) {
	return request<R>(path, "DELETE", JSON.stringify(body), null, params, token);
}

function request<R>(path: string, method: RequestType, body?: string, headers?: { [key: string]: string },
	query?: IIndexSignature<string | number>, token?: string) {

	let url = `${ baseURL }/${ path }`;

	if (query && Object.keys(query).length != 0) {
		url += `?${ encodeQueryParams(query) }`;
	}

	const authHeaders = { "Authorization": `Bearer ${ token }` };

	return new Promise<R>((resolve, reject) => {
		return fetch(url, {
			body,
			headers: { ...baseHeaders, ...authHeaders, ...headers },
			method,
			mode: "cors"
		})
			.then((response: Response) => {
				const { status } = response;

				if (status >= 200 && status < 300) {
					if (status === 204) {
						return resolve({ data: {} } as R);
					}

					resolve(response.json());
				} else if (status >= 400 && status < 500) {
					if (status === 401) {
						reject(401);
					} else {
						response.json().then((errorResult: IErrorResult) => {
							reject(flattenErrors(errorResult));
						});
					}
				} else if (status >= 500 && status < 600) {
					notify(NotificationType.ERROR, "Error", "An unknown error has occurred");
					reject();
				}
			}, (error) => {
				console.log(error);
			})
			.catch((error) => {
				console.log(error);
			});
	});
}

function encodeQueryParams(params: IIndexSignature<string | number>) {
	return Object.keys(params)
		.map((key) => {
			const encKey = encodeURIComponent(key);
			const encParam = encodeURIComponent(params[key]);

			return `${ encKey }=${ encParam }`;
		})
		.join("&");
}

const flattenErrors = (errorResult: IErrorResult) => {
	const { errors } = errorResult;
	const retVal = errors.map((error) => {
		return error.detail;
	}).reduce((messages: string[], currentMessage: string) => {
		return messages.concat(currentMessage);
	}, []);

	return retVal.join("\n");
};
